import type { CompactResourceThumbnail } from "@/desktop/compact-resource-events";
import type { WorkToastComputerScreenshot } from "@/stores/work-toast-store";

const SCREENSHOT_DISPLAY_CAP = 3;
const PENDING_SCREENSHOT_TTL_MS = 30_000;
const PENDING_EXECUTION_CAP = 64;

type Screenshots = WorkToastComputerScreenshot[];

/** Captures that arrived before any card existed for their execution. */
const pending = new Map<string, { screenshots: Screenshots; timer: number }>();

/** The latest capture only: its displays sit together by ordinal, and a
 *  thumbnail of another capture replaces all of them. */
export function mergeComputerScreenshot(
  current: Screenshots | undefined,
  thumbnail: CompactResourceThumbnail,
): Screenshots {
  const { captureId, ordinal, displayId } = thumbnail;
  const url = `data:image/jpeg;base64,${thumbnail.jpegBase64}`;
  const sameCapture = (current ?? []).filter(
    (shot) => shot.captureId === captureId && shot.ordinal !== ordinal,
  );
  return [...sameCapture, { captureId, ordinal, displayId, url }]
    .sort((a, b) => a.ordinal - b.ordinal)
    .slice(0, SCREENSHOT_DISPLAY_CAP);
}

export function stashPendingComputerScreenshot(
  thumbnail: CompactResourceThumbnail,
): void {
  const previous = pending.get(thumbnail.executionId);
  if (previous) window.clearTimeout(previous.timer);
  pending.set(thumbnail.executionId, {
    screenshots: mergeComputerScreenshot(previous?.screenshots, thumbnail),
    timer: window.setTimeout(
      () => pending.delete(thumbnail.executionId),
      PENDING_SCREENSHOT_TTL_MS,
    ),
  });
  while (pending.size > PENDING_EXECUTION_CAP) {
    const oldestKey = pending.keys().next().value;
    if (!oldestKey) break;
    const oldest = pending.get(oldestKey);
    if (oldest) window.clearTimeout(oldest.timer);
    pending.delete(oldestKey);
  }
}

/** Hands the held capture to the card that now exists; it is given out once. */
export function takePendingComputerScreenshots(
  executionId: string,
): Screenshots | null {
  const value = pending.get(executionId);
  if (!value) return null;
  window.clearTimeout(value.timer);
  pending.delete(executionId);
  return value.screenshots;
}

export function resetPendingComputerScreenshots(): void {
  for (const value of pending.values()) window.clearTimeout(value.timer);
  pending.clear();
}
